/**
 * MKT-258 / MKT-490 — Analytics / GA4 e-commerce dataLayer instrumentation.
 *
 * Central, typed wrapper around the `@ingresse/awa-analytics-web` SDK. Every
 * function here takes alchemist domain objects (Event, cart SummaryData, User,
 * fees, payment_type, transactionId), maps them to the canonical analytics
 * contract (Doc 2 taxonomy) and pushes the resulting node to `window.dataLayer`
 * via the SDK's `track(..., { destinations: ['gtm'] })`. GTM then
 * fans out to GA4 / Ads / the global Meta pixel and — via `organizer_tracking`
 * — the per-producer pixels.
 *
 * This is the SOLE client tracking path (MKT-490 cutover): the legacy bespoke
 * trackers (GoogleAnalytics gtag, FacebookPixel, MetaConversionsAPI) have been
 * removed and GTM now owns the fan-out. All work is guarded so analytics can
 * never break checkout: on failure we log in dev (via the app logger, which is a
 * no-op in production) and stay silent otherwise.
 *
 * Server-side Meta CAPI (MKT-484 dedup): for each funnel event we mint a shared
 * dedup id — the `transaction_id` for `purchase`, a random UUID otherwise —
 * publish it TOP-LEVEL on the dataLayer push as `meta_capi_dedup_id` (so the GTM
 * Meta pixel tag can read `{{meta_capi_dedup_id}}`) AND forward the SAME id to the
 * SDK `dispatchMetaCapi`, so Meta deduplicates the browser pixel and the server
 * event as one. Every funnel — resale included — publishes it under that one name
 * via `buildPushParams`; a funnel naming it differently double-counts silently.
 *
 * PII policy: contact data reaches the dataLayer as PLAIN TEXT, because the
 * Google/Meta tags hash it themselves inside GTM (Martech, 13 August 2026) and a
 * digest arriving where they expect plain text gets hashed twice — matching then
 * drops to zero silently. It flows ONLY through the SDK's `buildUserData`, which
 * normalizes it and emits a curated allowlist; the push is gated on `advertising`
 * consent and only carries a user when one is logged in. The Meta CAPI takes the
 * same fields raw, hashed by its own service (never hash here either).
 *
 * ── Taxonomy (Martech revision of 5 August 2026) ────────────────────────────
 *   item_id       (id)       = event.id            (the "product" is the EVENT)
 *   item_name     (group)    = event.title
 *   item_category (category) = event macro category (categories[0]?.name)
 *   item_category2           = ticket GROUP name   (`SummaryData.group`)
 *   item_category3           = ticket name         (`SummaryData.title`)
 *   item_variant  (name)     = fare type, e.g. Inteira / Meia (`category.name`)
 *
 * What changed vs. the 30 July revision: the event location LEFT the dataLayer, the
 * macro category took over `item_category`, and the ticket levels each moved up one
 * slot (group 3→2, name 4→3). Rationale (Martech, 5 August 2026): level 1 must mean
 * the same thing on every platform, and the location was the one field awa-purchase-ui
 * could not normalize to this app's "City - UF - Country" — its backend stores a
 * single formatted address string. So no app sends the location anymore.
 *
 * `item_category4` (lote) is NOT sent — the legacy model has no lote. What the
 * legacy payload calls a ticket `category` is the FARE TYPE (verified against the
 * api-embedstore tickets response, where it reads `{id:"1",name:"Inteira"}`), so
 * it feeds `item_variant`, not a category level.
 *
 * The ticket-level levels (2/3) and `item_variant` only exist from `add_to_cart`
 * on, which is where this app's funnel now starts.
 *
 * `view_item` is deliberately NOT emitted here (Martech decision, 5 August 2026).
 * The embedstore fired it in addition to the event page on site_next — which still
 * emits it and is the real item detail — and re-fired on every session/date change
 * because both ticket-list components called it from a `useEffect` keyed on the
 * carousel selection. Note the trade-off this accepts: a customer who deep-links
 * straight into the embedstore (producer link, QR, retargeting) now has no
 * `view_item` at all, so their GA4 funnel begins at `add_to_cart`.
 */
import {
  track,
  toEcommerce,
  toPaymentTypeLabel,
  buildOrganizerTracking,
  buildUserData,
  clearAnalyticsUser,
  dispatchMetaCapi,
  setAnalyticsUser,
  toMetaEventName,
  type AuthMethod,
  type EcommerceData,
  type EventName,
  type MetaCapiCustomData,
  type MetaCapiUserData,
  type OrganizerTracking,
  type RawUserData,
  type ToEcommerceItemSource,
  type ToEcommerceSource,
  type TrackDestination,
  type UserData,
} from '@ingresse/awa-analytics-web';

import { SummaryData } from '../types/cart';
import { CouponDetail } from '../types/cupom';
import { Event } from '../types/event';
import { User } from '../types/user';
import { logger } from '../utils/logger';
import { splitFullName } from '../utils/splitFullName';

/** Touch only the dataLayer — the app keeps its own PostHog / CleverTap. */
const GTM_ONLY: TrackDestination[] = ['gtm'];

/** sessionStorage key prefix guarding the purchase against double-firing. */
const PURCHASE_DEDUP_PREFIX = 'analytics_purchase_';

/** Minimal session-user shape the analytics identity needs (subset of `User`). */
type AnalyticsSessionUser = Pick<User, 'id' | 'email' | 'phone'>;

/**
 * Register (or clear) the logged-in user in the SDK identity store so EVERY
 * dataLayer event auto-carries the root `user_id` + `user_data`
 * (Doc 1 §2 — "user_id/user_data só se logado"). Call whenever the session user
 * changes: hydrate, login and logout/expiry (`null` clears the identity from the
 * GTM model immediately). Name is intentionally NOT sent — `user_data.address`
 * was dropped (MKT-666). Fire-and-forget: the SDK call is async (it predates the
 * move to plain text) and must never block or break checkout.
 */
export function syncAnalyticsUser(user: AnalyticsSessionUser | null): void {
  try {
    if (!user) {
      clearAnalyticsUser();
      return;
    }

    void setAnalyticsUser({
      user_id : user.id,
      email : user.email || undefined,
      phone : user.phone ? { ddi : user.phone.ddi, number : user.phone.number } : undefined,
    }).catch((error: unknown) => {
      logger.error('[analyticsDataLayer] setAnalyticsUser failed', {
        reason : error instanceof Error ? error.message : 'unknown',
      });
    });
  } catch (error) {
    logger.error('[analyticsDataLayer] syncAnalyticsUser failed', {
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
}

/** The two canonical GA4 auth events. */
export type AuthEventName = 'login' | 'sign_up';

/** Minimal identity the auth events need (the iframe `done` payload subset). */
export type AuthEventUser = {
  id: string | number;
  email?: string;
  phone?: { ddi: number; number: string };
};

/**
 * MKT-486 — emit the canonical GA4 `login` / `sign_up`.
 *
 * ── Why the HOST owns these events ──────────────────────────────────────────
 * The auth UI runs inside the auth.ingresse.com iframe, whose `bootstrap()` ALWAYS
 * loads a GTM container (the SDK's embedded detection only suppresses the consent
 * BANNER, not GTM). Emitting there put the events in the IFRAME's dataLayer, with
 * `page_location` = auth.ingresse.com and a GA4 session detached from checkout.
 * The iframe now only NOTIFIES (`AUTH_LOGIN_SUCCESS` / `AUTH_REGISTER_SUCCESS`,
 * carrying `method`); this host emits into the real container. See `useAuthing`.
 *
 * Replaces the legacy `GoogleAnalytics.trackLoginEvent(LOGIN, { method: 'Continue' })`
 * that this branch deletes — same event, but with a faithful `method` instead of
 * the hardcoded 'Continue', plus `user_id`/`user_data`.
 *
 * `user_id` and `user_data` are MANDATORY on `LoginEvent`/`SignUpEvent`, and the SDK
 * auto-attaches both from its identity store. Hashing is async, so `setAnalyticsUser`
 * is AWAITED here — the fire-and-forget `syncAnalyticsUser` would race and let the
 * event go out without `user_data`.
 */
export async function trackAuthEvent(
  event: AuthEventName,
  user: AuthEventUser,
  method: AuthMethod,
  ingresseEvent?: Event | null,
): Promise<void> {
  const userId = String(user.id);

  try {
    await setAnalyticsUser({
      user_id : userId,
      email : user.email || undefined,
      phone : user.phone ? { ddi : user.phone.ddi, number : user.phone.number } : undefined,
    });
  } catch (error) {
    // Identity failed — still emit rather than losing the conversion.
    logger.error('[analyticsDataLayer] trackAuthEvent identity failed', {
      event,
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }

  try {
    // `organizer_tracking` on login/sign_up too (Martech, 13 August 2026): these
    // fire INSIDE the purchase flow, so the producer's pixels have to see the
    // authentication as part of that event's funnel — without it the container
    // cannot attribute the login to the producer, and the per-producer Meta/Ads
    // tags never fire for it. `ingresseEvent` is optional so an auth outside an
    // event context (if one ever appears) still emits, just without the node.
    const organizer_tracking = ingresseEvent ? buildOrganizer(ingresseEvent) : undefined;

    track(
      event,
      { method, ...(organizer_tracking && { organizer_tracking }) },
      { destinations : GTM_ONLY },
    );
  } catch (error) {
    logger.error('[analyticsDataLayer] trackAuthEvent failed', {
      event,
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
}

type CartEventParams = {
  event: Event | null;
  summaryData: SummaryData[];
  couponApplied: CouponDetail | null;
  /**
   * The applied "código" (`passKeyApplied` from the event context) → `discount_code`.
   *
   * Martech clarification (5 August 2026): the platform discounts a ticket two ways
   * and the contract keeps them apart — "cupom" is `coupon` (from `couponApplied`)
   * and "código" is `discount_code`. Never populate one from the other; they are
   * different mechanisms that can be active at the same time.
   */
  passKeyApplied?: string | null;
  user: User | null;
};

type AddPaymentInfoParams = CartEventParams & {
  /** Raw payment method from the checkout iframe (e.g. "creditCard", "pix"). */
  paymentType?: string | null;
};

/**
 * MKT-683 — a callback payload from the checkout iframe, carrying the amounts the
 * gateway priced. `IPricingDetails` comes from `onPricing`/`onPaymentInfo` (the
 * pre-payment funnel) and `IPaymentDetails` from `onSuccess`. Optional because
 * the instant-approval path has no iframe at all.
 */
type GatewayDetailsParam = {
  gatewayDetails?: IPaymentDetails | IPricingDetails | null;
};

type PurchaseParams = CartEventParams &
  GatewayDetailsParam & {
    transactionId: string | null;
    paymentType?: string | null;
    /** Affiliation / clientUrl the purchase originated from. */
    affiliation?: string;
  };

/** organizer_tracking for an event — undefined when no producer tracking IDs exist. */
function buildOrganizer(event: Event): OrganizerTracking | undefined {
  const trackingTags = event.attributes?.trackingTags?.value;
  return buildOrganizerTracking({
    ing_event_id : event.id,
    producer_id : event.producerId,
    facebook_pixel_id : trackingTags?.facebook?.id,
    ga4_measurement_id : trackingTags?.googleAnalytics?.id,
    // MKT-518 — forward the complete per-producer tracker set. The SDK dictionary
    // maps these fields and omits any that are undefined, so this is safe even
    // before the legacy backend starts sending them.
    google_ads_id : trackingTags?.googleAds?.id,
    google_ads_label : trackingTags?.googleAds?.label,
    tiktok_pixel_id : trackingTags?.tiktok?.id,
    twitter_pixel_id : trackingTags?.twitter?.id,
  });
}

/**
 * The contact fields `user_data` accepts — the ONLY shape any funnel may send.
 * Deliberately narrower than `User`: a field that cannot be named here cannot
 * reach the dataLayer by accident.
 */
type AnalyticsContact = {
  email?: string;
  phone?: { ddi: number; number: string };
};

/**
 * The GA4 `user_data` node — undefined when no user is logged in. Plain text by
 * contract: the SDK normalizes, GTM hashes.
 *
 * Must stay field-for-field identical to what `syncAnalyticsUser` registers in the
 * SDK identity store: the push assigns `params.user_data` explicitly and the SDK's
 * `withAnalyticsUser` spreads the payload LAST (`{...identity, ...payload}`), so
 * this object OVERRIDES the identity-store one on every event that carries it.
 * Any field only present here would make `user_data` change shape mid-funnel.
 *
 * Which is why BOTH funnels (main and resale) come through here. The resale push
 * used to call `buildUserData` itself with `name` + `external_id`; SDK 2.0.0
 * ignores both, so the fields were dead — but the divergence was one SDK release
 * away from putting the buyer's name in the dataLayer on resale only, which is
 * exactly what MKT-666 removed. `external_id` is likewise not sent: it only ever
 * duplicated the root `user_id` that already rides on every event.
 *
 * The Meta CAPI is a separate channel and does want the name: see `buildRawUser`.
 */
async function buildUser(contact: AnalyticsContact | null | undefined): Promise<UserData | undefined> {
  if (!contact) return undefined;

  const raw: RawUserData = {
    email : contact.email || undefined,
    phone : contact.phone ? { ddi : contact.phone.ddi, number : contact.phone.number } : undefined,
  };

  return buildUserData(raw);
}

/**
 * Contact fields for the server-side Meta CAPI. The `meta-conversions-api`
 * service hashes email/phone/first_name/last_name — NEVER hash them here
 * (double-hashing breaks Meta matching). Returns undefined when no
 * user is logged in so the CAPI event is sent without user data.
 */
function buildRawUser(user: User | null | undefined): MetaCapiUserData | undefined {
  if (!user) return undefined;

  const { first, last } = splitFullName(user.name);

  return {
    email : user.email || undefined,
    phone : user.phone?.number || undefined,
    country_code : user.phone?.ddi != null ? String(user.phone.ddi) : undefined,
    first_name : first || undefined,
    last_name : last || undefined,
    external_id : String(user.id),
  };
}

/**
 * A fresh dedup id for a non-purchase funnel event (purchase reuses its
 * `transaction_id`). Prefers `crypto.randomUUID` and never throws — a failure
 * must not break checkout — falling back to a timestamp+random id.
 */
function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (error) {
    logger.error('[analyticsDataLayer] crypto.randomUUID failed', {
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Common item fields shared by every funnel event (the EVENT is the product). */
function buildEventBaseItem(event: Event): ToEcommerceItemSource {
  return {
    id : event.id,
    group : event.title,
    // Taxonomy revision (Martech, 5 August 2026): `item_category` = macro category.
    // The event location used to sit here and is no longer sent at all.
    category : event.categories?.[0]?.name,
  };
}

/** Round to 2 decimals — currency amounts, and float sums drift. */
function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * What this cart line ACTUALLY costs the buyer, for the whole quantity.
 *
 * `accumulatedValueInCart` is the authoritative number: `ApplyCouponRuleIfExists`
 * prices each unit individually and sums them, which is the only source that
 * survives a coupon with `limit_ticket`. Under such a coupon a single line is
 * charged at TWO prices — `processTicketsWithActiveCoupon` spends the coupon
 * budget unit by unit (and the counter is shared across lines), charging
 * `originalPrice` for whatever does not fit — so no single unit price describes
 * the line and `price × quantity` under-reports it.
 *
 * Falls back to `price × quantity` only if the field is unusable, which should not
 * happen: `summaryData` is produced by `prepareSummaryData`, which `applyCartRules`
 * calls right after running that rule.
 */
function toLineChargedTotal(ticket: SummaryData): number | undefined {
  const { accumulatedValueInCart, price, quantity } = ticket;

  if (typeof accumulatedValueInCart === 'number' && Number.isFinite(accumulatedValueInCart)) {
    return accumulatedValueInCart;
  }

  return typeof price === 'number' && Number.isFinite(price) ? price * quantity : undefined;
}

/**
 * The authoritative order value: Σ of what each line actually costs. `undefined`
 * when the cart cannot produce it, so the SDK falls back to deriving it.
 *
 * The legacy checkout sent exactly this (`summaryData.reduce(… accumulatedValueInCart)`)
 * and the cutover dropped it, leaving the SDK to derive `value` from Σ price ×
 * quantity. That silently under-reported every purchase made under a coupon with a
 * partial `limit_ticket` — see `toLineChargedTotal`. On the instant-approval path
 * there is no gateway callback to supply the real total, so nothing corrected it.
 *
 * Fees are NOT included, matching the legacy: they travel in `service_fee` /
 * `processing_fee` / `protection_fee` and the SDK aggregates them into `tax`.
 */
function computeCartValue(summaryData: SummaryData[]): number | undefined {
  const lines = summaryData.filter((ticket) => ticket.quantity > 0);
  if (lines.length === 0) return undefined;

  let total = 0;
  for (const line of lines) {
    const charged = toLineChargedTotal(line);
    if (charged === undefined) return undefined;
    total += charged;
  }

  return round2(total);
}

/**
 * The per-unit discount actually granted on this cart line, in CURRENCY.
 *
 * Derived from the PRICED cart — `originalPrice × quantity` minus what the line
 * really costs — never re-derived from the coupon metadata. `useRules.applyCoupon`
 * already resolved percentual-vs-absolute, and `ApplyCouponRuleIfExists` decides,
 * unit by unit, which ones the coupon actually paid for. So the difference IS the
 * discount granted, and it is 0 exactly where nothing was discounted — including
 * the lines an active coupon does not cover, which it restores to `originalPrice`.
 *
 * Divided by `quantity` because GA4 reads `discount` as a PER-UNIT amount, the
 * same scope as `price`: reporting the line total here would multiply it by the
 * quantity again in the reports.
 *
 * Three bugs this replaces, all live only under a coupon:
 *   - re-deriving from `coupon.value_discount` over `ticket.price`, a base ALREADY
 *     discounted, reported 9 instead of 10 for a 10% coupon on a R$100 ticket;
 *   - it ran for EVERY line while a coupon was active, inventing a discount on
 *     lines the coupon never covered;
 *   - `originalPrice - price` fixed both but still assumed EVERY unit of the line
 *     got the discount, over-reporting it under a partial `limit_ticket`.
 */
function toLineDiscount(ticket: SummaryData): number {
  const { originalPrice, quantity } = ticket;
  if (typeof originalPrice !== 'number' || !(quantity > 0)) return 0;

  const charged = toLineChargedTotal(ticket);
  if (charged === undefined) return 0;

  return Math.max(round2((originalPrice * quantity - charged) / quantity), 0);
}

/** Map cart SummaryData lines (quantity > 0) to canonical e-commerce items. */
function buildLineItems(
  event: Event,
  summaryData: SummaryData[],
  couponApplied: CouponDetail | null,
  passKeyApplied?: string | null,
): ToEcommerceItemSource[] {
  const base = buildEventBaseItem(event);
  const couponName = couponApplied?.coupon?.name;

  return summaryData
    .filter((ticket) => ticket.quantity > 0)
    .map((ticket) => {
      // Per-line: a coupon covers only the ticket types it was issued for, and the
      // amount it took off depends on that line's own price.
      const discount = toLineDiscount(ticket);

      return {
        ...base,
        // Taxonomy revision (Martech, 5 August 2026) — the ticket levels, which only
        // exist from `add_to_cart` on. The legacy hierarchy is
        // Ticket (group) → TicketType (ticket) → TicketType.category (the fare type):
        //   item_category2 = ticket group name        (`SummaryData.group`)
        //   item_category3 = ticket name              (`SummaryData.title`)
        //   item_variant   = fare type, e.g. Inteira  (`SummaryData.category.name`)
        // `item_category4` (lote) is NOT sent: the legacy model has no lote — the
        // field named `category` here is the fare type, not a lote (verified against
        // the api-embedstore tickets payload, where it reads `{id:"1",name:"Inteira"}`).
        item_category2 : ticket.group,
        item_category3 : ticket.title,
        name : ticket.category?.name,
        price : ticket.price,
        quantity : ticket.quantity,
        ...(couponName ? { coupon : couponName } : {}),
        // "código" — a separate contract field from `coupon`, never a fallback for
        // it. See CartEventParams.passKeyApplied.
        ...(passKeyApplied ? { discount_code : passKeyApplied } : {}),
        ...(discount > 0 ? { discount } : {}),
      };
    });
}

/** Total Ingresse service fee: Σ (per-unit tax × quantity) over the cart. */
function computeServiceFee(summaryData: SummaryData[]): number {
  return summaryData
    .filter((ticket) => ticket.quantity > 0)
    .reduce(
      (acc, ticket) => acc + (typeof ticket.tax === 'number' ? ticket.tax * ticket.quantity : 0),
      0,
    );
}

/**
 * Gateway money is an integer in cents; the dataLayer contract (Doc 1 §1d) wants
 * a decimal amount in the currency unit.
 */
function fromCents(cents?: number | null): number | undefined {
  return typeof cents === 'number' && Number.isFinite(cents) ? cents / 100 : undefined;
}

/**
 * MKT-683 — map the gateway success callback onto the ecommerce source.
 *
 * The gateway is the source of truth for what was actually charged: interest and
 * the processing fee depend on the payment method and the installment plan chosen
 * INSIDE the checkout iframe, which the local cart cannot know. Only the iframe
 * flow carries this payload — the instant-approval path (payment-confirmed.tsx)
 * has none, and there we keep deriving the totals from the cart.
 *
 * `tax` is left for the SDK to aggregate from the fee fields (service +
 * processing + protection), which is the contract's definition. Interest is not
 * part of the Martech taxonomy, so it only lands in `value` (total charged).
 */
function fromGatewayDetails(
  details?: IPaymentDetails | IPricingDetails | null,
): Partial<ToEcommerceSource> | undefined {
  if (!details) return undefined;

  const value = fromCents(details.amount);
  const serviceFee = fromCents(details.service_fee);
  const processingFee = fromCents(details.gateway_fee);
  const protectionFee = fromCents(details.protection_fee);

  return {
    ...(value !== undefined ? { value } : {}),
    ...(details.currency ? { currency : details.currency } : {}),
    ...(details.coupon ? { coupon : details.coupon } : {}),
    ...(serviceFee !== undefined ? { service_fee : serviceFee } : {}),
    ...(processingFee !== undefined ? { processing_fee : processingFee } : {}),
    ...(protectionFee !== undefined ? { protection_fee : protectionFee } : {}),
    ...(details.has_purchase_protection !== undefined
      ? { has_purchase_protection : details.has_purchase_protection }
      : {}),
  };
}

/**
 * MKT-683 — the event-level parameters the contract expects at the ROOT of the
 * push, alongside `event`: the fee breakdown and (after the method is chosen)
 * `payment_type`. That is where the GTM setup reads them from (`dlv - service_fee`,
 * `dlv - payment_type`, … — Doc 1 §E/§F/§G); a value living only inside
 * `ecommerce` comes back empty in those tags. The fees are intentionally in BOTH
 * places — GA4 reads `tax` natively off the ecommerce node while the per-fee
 * custom dimensions are mapped from the root.
 *
 * `user_id` is not listed here: the SDK auto-attaches it from its identity store
 * (`syncAnalyticsUser`).
 */
function rootParams(
  gatewayDetails?: IPaymentDetails | IPricingDetails | null,
  paymentType?: string | null,
): Record<string, unknown> {
  const fees = fromGatewayDetails(gatewayDetails);
  const label = toPaymentTypeLabel(paymentType);

  return {
    ...(fees?.service_fee !== undefined ? { service_fee : fees.service_fee } : {}),
    ...(fees?.processing_fee !== undefined ? { processing_fee : fees.processing_fee } : {}),
    ...(fees?.protection_fee !== undefined ? { protection_fee : fees.protection_fee } : {}),
    ...(fees?.has_purchase_protection !== undefined
      ? { has_purchase_protection : fees.has_purchase_protection }
      : {}),
    ...(label ? { payment_type : label } : {}),
  };
}

function purchaseAlreadyTracked(transactionId: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(`${PURCHASE_DEDUP_PREFIX}${transactionId}`) !== null;
  } catch (error) {
    // Fail open (allow the push) but surface the reason in dev.
    logger.error('[analyticsDataLayer] purchase dedup read failed', {
      reason : error instanceof Error ? error.message : 'unknown',
    });
    return false;
  }
}

function markPurchaseTracked(transactionId: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(`${PURCHASE_DEDUP_PREFIX}${transactionId}`, '1');
  } catch (error) {
    logger.error('[analyticsDataLayer] purchase dedup write failed', {
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * Fire the server-side Meta CAPI for the funnel events Meta accepts (via
 * `toMetaEventName`), reusing the SAME `eventId` as the GTM Meta pixel so the two
 * channels deduplicate (MKT-484). RAW contact fields are sent as-is — the SDK
 * service hashes them. Skips events without a Meta mapping. Fire-and-forget.
 */
function dispatchServerCapi(
  name: EventName,
  ecommerce: EcommerceData,
  eventId: string,
  event: Event | null,
  user: User | null | undefined,
): void {
  const metaName = toMetaEventName(name);
  if (!metaName || !event) return;

  const eventDbId = String(event.id);
  const customData: MetaCapiCustomData = {
    currency : ecommerce.currency,
    value : ecommerce.value,
    content_type : 'product',
    content_ids : [eventDbId],
    contents : ecommerce.items.map((item) => ({
      id : eventDbId,
      quantity : item.quantity,
      item_price : item.price,
    })),
  };

  void dispatchMetaCapi({
    eventName : metaName,
    eventId,
    userData : buildRawUser(user),
    customData,
  });
}

/**
 * Assemble the dataLayer payload every funnel pushes: the ecommerce node, the
 * Meta CAPI dedup id, `organizer_tracking`, `user_data` and the event-level extras.
 *
 * BOTH funnels (main and resale) build their push here, on purpose. The resale
 * push used to assemble its own object and still called the dedup id `event_id`
 * after the main funnel had been renamed — so the GTM Meta tag read
 * `{{meta_capi_dedup_id}}`, came back empty, and every resale conversion was
 * counted twice (pixel + CAPI). One assembler means one name, and a rename can no
 * longer reach only half the funnels.
 */
function buildPushParams(input: {
  ecommerce: EcommerceData;
  /** Shared dedup id — published here AND sent on the CAPI body (MKT-484). */
  eventId: string;
  organizer?: OrganizerTracking;
  userData?: UserData;
  extra?: Record<string, unknown>;
}): Record<string, unknown> {
  const params: Record<string, unknown> = {
    ecommerce : input.ecommerce,
    // Top-level so the GTM Meta pixel tag can read it and dedup against the CAPI
    // event carrying the same id (MKT-484). Named `meta_capi_dedup_id` rather
    // than `event_id` (Martech, 13 August 2026): under the generic name the
    // review read it as an unexplained value and asked for its removal, which
    // would have double-counted every Meta conversion.
    meta_capi_dedup_id : input.eventId,
  };

  if (input.organizer) params.organizer_tracking = input.organizer;
  if (input.userData) params.user_data = input.userData;
  if (input.extra) Object.assign(params, input.extra);

  return params;
}

/**
 * Assemble the dataLayer payload, push it via the SDK, then fire the server-side
 * Meta CAPI with the same dedup id. Fully guarded — never throws.
 */
async function emit(
  name: EventName,
  ecommerce: EcommerceData,
  options: {
    event: Event | null;
    /** Shared dedup id — top-level on the push and forwarded to the Meta CAPI. */
    eventId: string;
    user?: User | null;
    includeUserData?: boolean;
    extra?: Record<string, unknown>;
  },
): Promise<void> {
  try {
    const params = buildPushParams({
      ecommerce,
      eventId : options.eventId,
      organizer : options.event ? buildOrganizer(options.event) : undefined,
      userData :
        options.includeUserData && options.user ? await buildUser(options.user) : undefined,
      extra : options.extra,
    });

    track(name, params, { destinations : GTM_ONLY });

    dispatchServerCapi(name, ecommerce, options.eventId, options.event, options.user);
  } catch (error) {
    logger.error('[analyticsDataLayer] failed to push event', {
      name,
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * Producer platform ids inside `organizer_tracking`. `ing_event_id` and
 * `producer_id` are deliberately absent: they identify the producer, not a
 * destination the container can fire. `google_ads_label` too — it is a modifier
 * of `google_ads_id`, never a destination on its own.
 */
const ORGANIZER_PLATFORM_KEYS = [
  'meta_pixel_id',
  'google_ads_id',
  'tiktok_pixel_id',
  'x_pixel_id',
  'ga4_measurement_id',
] as const;

/**
 * `organizer_context` — the `organizer_tracking` node in an event of its own
 * (Martech, 13 August 2026). Pages that fire no e-commerce event still need to
 * hand the producer's pixel ids to the container, so it gains a dedicated
 * trigger instead of depending on a funnel event existing.
 *
 * Skipped when the producer has NO platform registered: with every id `null`
 * there is nothing for the container to fire and the push would only add noise.
 * Individual ids stay `null` when just one platform is missing, as before.
 *
 * MIND when reading this node in GTM: the value does NOT persist onto later
 * events. The SDK pushes a cleanup zeroing `organizer_tracking` before each
 * event — precisely so one producer's ids never leak into another producer's
 * event — and every funnel event carries its own copy. Tags must read the node
 * off the event they fire on, not off this one.
 */
export function trackOrganizerContext(event: Event | null | undefined): void {
  try {
    if (!event) return;

    const organizer = buildOrganizer(event);
    if (!organizer) return;

    const hasPlatform = ORGANIZER_PLATFORM_KEYS.some(
      (key) => organizer[key] !== null && organizer[key] !== undefined,
    );
    if (!hasPlatform) return;

    track('organizer_context', { organizer_tracking : organizer }, { destinations : GTM_ONLY });
  } catch (error) {
    logger.error('[analyticsDataLayer] failed to push organizer_context', {
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * `add_to_cart` — fired on the naming page. Items = only cart lines with
 * quantity > 0. No user_data at this stage (per Doc 2 — identification happens
 * later in the funnel).
 */
export function trackAddToCart({
  event,
  summaryData,
  couponApplied,
  passKeyApplied,
  user,
}: CartEventParams): void {
  if (!event) return;

  const items = buildLineItems(event, summaryData, couponApplied, passKeyApplied);
  if (items.length === 0) return;

  const ecommerce = toEcommerce({
    currency : event.accepted_currencies,
    coupon : couponApplied?.coupon?.name,
    // "código" — see CartEventParams.passKeyApplied.
    discount_code : passKeyApplied ?? undefined,
    // The authoritative cart total — see `computeCartValue`.
    value : computeCartValue(summaryData),
    items,
  });

  // No user_data on the dataLayer at this stage (Doc 2), but the CAPI
  // still gets the RAW user when logged in to improve Meta match quality.
  void emit('add_to_cart', ecommerce, { event, eventId : newEventId(), user });
}

/**
 * `begin_checkout` — fired when the payment step starts. Carries the aggregated
 * service fee (tax) and user_data.
 */
export function trackBeginCheckout({
  event,
  summaryData,
  couponApplied,
  passKeyApplied,
  user,
  gatewayDetails,
}: CartEventParams & GatewayDetailsParam): void {
  if (!event) return;

  const items = buildLineItems(event, summaryData, couponApplied, passKeyApplied);
  if (items.length === 0) return;

  const serviceFee = computeServiceFee(summaryData);

  const ecommerce = toEcommerce({
    currency : event.accepted_currencies,
    coupon : couponApplied?.coupon?.name,
    // "código" — see CartEventParams.passKeyApplied.
    discount_code : passKeyApplied ?? undefined,
    service_fee : serviceFee > 0 ? serviceFee : undefined,
    // The authoritative cart total — see `computeCartValue`. Spread below, the
    // gateway's own `amount` still wins whenever the callback carries one.
    value : computeCartValue(summaryData),
    items,
    // MKT-683 — the amounts the gateway actually priced win over the cart estimate.
    ...fromGatewayDetails(gatewayDetails),
  });

  void emit('begin_checkout', ecommerce, {
    event,
    eventId : newEventId(),
    user,
    // Doc 1 §E — the fee breakdown at the root. No `payment_type`: the method is
    // not chosen yet at this point in the funnel.
    extra : rootParams(gatewayDetails),
    includeUserData : true,
  });
}

/**
 * `add_payment_info` — fired once the user picks a payment method. Includes the
 * normalized `payment_type` and user_data.
 */
export function trackAddPaymentInfo({
  event,
  summaryData,
  couponApplied,
  passKeyApplied,
  user,
  paymentType,
  gatewayDetails,
}: AddPaymentInfoParams & GatewayDetailsParam): void {
  if (!event) return;

  const items = buildLineItems(event, summaryData, couponApplied, passKeyApplied);
  if (items.length === 0) return;

  const serviceFee = computeServiceFee(summaryData);
  const root = rootParams(gatewayDetails, paymentType);

  const ecommerce = toEcommerce({
    currency : event.accepted_currencies,
    coupon : couponApplied?.coupon?.name,
    // "código" — see CartEventParams.passKeyApplied.
    discount_code : passKeyApplied ?? undefined,
    service_fee : serviceFee > 0 ? serviceFee : undefined,
    // The authoritative cart total — see `computeCartValue`. Spread below, the
    // gateway's own `amount` still wins whenever the callback carries one.
    value : computeCartValue(summaryData),
    items,
    // MKT-683 — the amounts the gateway actually priced win over the cart estimate.
    ...fromGatewayDetails(gatewayDetails),
    // Doc 1 §F nests a copy of `payment_type` on this event only — "repetido
    // dentro para garantir leitura nativa". `purchase` (§G) keeps the root one.
    ...(root.payment_type ? { payment_type : String(root.payment_type) } : {}),
  });

  void emit('add_payment_info', ecommerce, {
    event,
    eventId : newEventId(),
    user,
    includeUserData : true,
    extra : root,
  });
}

/**
 * `purchase` — the canonical conversion. Guarded so it fires at most once per
 * `transaction_id` per session (Doc 1 dedup). Includes the gateway
 * transaction_id, normalized payment_type and user_data.
 */
export function trackPurchase({
  event,
  summaryData,
  couponApplied,
  passKeyApplied,
  user,
  transactionId,
  paymentType,
  affiliation,
  gatewayDetails,
}: PurchaseParams): void {
  if (!event) return;

  const txId = transactionId ? String(transactionId).trim() : '';
  if (!txId || txId === 'undefined' || txId === 'null') return;

  if (purchaseAlreadyTracked(txId)) return;

  const items = buildLineItems(event, summaryData, couponApplied, passKeyApplied);
  if (items.length === 0) return;

  const serviceFee = computeServiceFee(summaryData);
  const root = rootParams(gatewayDetails, paymentType);

  const ecommerce = toEcommerce({
    currency : event.accepted_currencies,
    transaction_id : txId,
    coupon : couponApplied?.coupon?.name,
    // "código" — see CartEventParams.passKeyApplied.
    discount_code : passKeyApplied ?? undefined,
    service_fee : serviceFee > 0 ? serviceFee : undefined,
    // The authoritative cart total — see `computeCartValue`. This is THE fix for
    // the instant-approval path: no callback there, so without it `value` fell
    // back to Σ price × quantity and under-reported the conversion.
    value : computeCartValue(summaryData),
    items,
    // MKT-683 — what the gateway actually charged wins over the cart estimate.
    // Spread last so every field it carries overrides the local derivation; on the
    // instant-approval path there is no callback and the cart values stand.
    ...fromGatewayDetails(gatewayDetails),
    // A copia aninhada FALTAVA aqui: o contrato antigo mantinha o `payment_type` so
    // na raiz no `purchase` e o aninhava apenas no `add_payment_info`, entao o
    // evento de conversao reportava metodo de pagamento nenhum dentro do no.
    // Achado pelo Caique na revisao de 13 August 2026.
    ...(root.payment_type ? { payment_type : String(root.payment_type) } : {}),
  });

  // Mark synchronously BEFORE the async push so a re-render / StrictMode
  // double-invoke cannot enqueue a second purchase for the same transaction.
  markPurchaseTracked(txId);

  // Purchase reuses the transaction_id as the shared dedup id: the GTM Meta tag
  // reads `{{ecommerce.transaction_id}}` and the CAPI Purchase carries the same
  // id, so Meta collapses the two channels into a single conversion (MKT-484).
  void emit('purchase', ecommerce, {
    event,
    eventId : txId,
    user,
    includeUserData : true,
    extra : {
      ...root,
      // Doc 1 §G puts `transaction_id` at the root as well as inside `ecommerce`.
      transaction_id : txId,
      ...(affiliation ? { affiliation } : {}),
    },
  });
}

/** Minimal buyer shape for the resale flow (its user context differs from the main `User`). */
type ResaleUserLike = {
  id: string | number;
  email?: string;
  name?: string;
};

/**
 * Explicit primitives for a resale purchase — the resale flow has no cart /
 * SummaryData / `Event`, so it can't reuse `CartEventParams`. The caller maps
 * `resaleData`/`resaleEvent` into these.
 */
export type ResalePurchaseParams = {
  /** Platform event id → `item_id` (kept consistent with the main funnel). */
  eventId: string | number | null;
  eventTitle?: string;
  producerId?: string | number;
  /**
   * `item_category` (macro category) is NOT sent on resale: `resaleEvent` carries
   * no `categories`, and the event location that used to fill this slot left the
   * dataLayer entirely in the 5 August 2026 revision. Level 1 is legitimately
   * absent here rather than filled with a differently-shaped value.
   */
  /** Ticket guest-type label (fare type) → `item_variant`. */
  ticketVariant?: string;
  transactionId: string | null;
  /** Amount actually paid (FastPass final price or the resale sale value). */
  value: number;
  currency?: string;
  user?: ResaleUserLike | null;
  affiliation?: string;
};

/**
 * Push the resale `purchase` to the dataLayer (with user_data) and fire the
 * server-side Meta CAPI with the SAME dedup id (`= transaction_id`).
 * `organizer_tracking` carries only `ing_event_id` and `producer_id` — the resale
 * event exposes no producer pixel ids, so the remaining keys go out `null`.
 */
async function emitResalePurchase(
  ecommerce: EcommerceData,
  eventId: string,
  ctx: {
    ingEventId: string | number;
    producerId?: string | number;
    user?: ResaleUserLike | null;
    affiliation?: string;
  },
): Promise<void> {
  try {
    // Same assembler as the main funnel — including the `meta_capi_dedup_id` name
    // the GTM Meta tag reads. `user_data` likewise goes through `buildUser`, so the
    // resale `purchase` cannot drift into a different shape (the buyer's `name`
    // used to be passed here and nowhere else).
    const params = buildPushParams({
      ecommerce,
      eventId,
      organizer : buildOrganizerTracking({
        ing_event_id : ctx.ingEventId,
        producer_id : ctx.producerId,
      }),
      userData : await buildUser(ctx.user),
      extra : ctx.affiliation ? { affiliation : ctx.affiliation } : undefined,
    });

    track('purchase', params, { destinations : GTM_ONLY });

    const { first, last } = ctx.user?.name ? splitFullName(ctx.user.name) : { first : '', last : '' };
    const capiUser: MetaCapiUserData | undefined = ctx.user
      ? {
        email : ctx.user.email || undefined,
        first_name : first || undefined,
        last_name : last || undefined,
        external_id : String(ctx.user.id),
      }
      : undefined;

    void dispatchMetaCapi({
      eventName : 'Purchase',
      eventId,
      userData : capiUser,
      customData : {
        currency : ecommerce.currency,
        value : ecommerce.value,
        content_type : 'product',
        content_ids : [String(ctx.ingEventId)],
        contents : ecommerce.items.map((item) => ({
          id : String(ctx.ingEventId),
          quantity : item.quantity,
          item_price : item.price,
        })),
      },
    });
  } catch (error) {
    logger.error('[analyticsDataLayer] resale purchase failed', {
      reason : error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * `purchase` for the RESALE (secondary-market) flow. Same shared dedup id as the
 * main purchase (`meta_capi_dedup_id = transaction_id`) so the GTM Meta pixel and
 * the CAPI collapse into one Meta conversion. Guarded; fires at most once per
 * `transaction_id` per session.
 */
export function trackResalePurchase(params: ResalePurchaseParams): void {
  const { eventId, eventTitle, producerId, ticketVariant, transactionId, value, currency, user, affiliation } =
    params;
  if (eventId == null) return;

  const txId = transactionId ? String(transactionId).trim() : '';
  if (!txId || txId === 'undefined' || txId === 'null') return;
  if (purchaseAlreadyTracked(txId)) return;

  const ecommerce = toEcommerce({
    currency : currency || 'BRL',
    transaction_id : txId,
    value,
    items : [
      {
        id : eventId,
        group : eventTitle,
        ...(ticketVariant ? { name : ticketVariant } : {}),
        // No category level here — see ResalePurchaseParams.
        price : value,
        quantity : 1,
      },
    ],
  });

  // Mark synchronously before the async push (re-render / StrictMode guard).
  markPurchaseTracked(txId);

  void emitResalePurchase(ecommerce, txId, { ingEventId : eventId, producerId, user, affiliation });
}
